import { useQueryClient } from '@tanstack/react-query';
import type { DatoveSchrankyDatovaZpravaDto } from '@gov-nx/api';
import {
	archiveDataBoxesMessageQuery,
	DatovkaNastaveniArchivaceValue,
	deleteDataBoxesMessageFromVaultQuery,
	deleteDataBoxesMessageQuery,
	restoreDataBoxesMessageQuery,
	useArchivingSettingsQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import {
	useDataBoxEvents,
	useDownloadEvents,
	useMessageEvents,
} from '@gov-nx/core/events';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { PageCode } from '@gov-nx/module/page';
import { dateDiffInDays } from '@gov-nx/utils/common';
import type {
	DataBoxConfirmModalType,
	DataBoxFolderType,
} from '../providers/DataBoxes.types';
import { DataBoxMessageDetailLoadingItemType } from '../providers/MessageDetailContext.types';
import { useSaveAllAttachmentsToDocuments } from './useSaveAllAttachmentsToDocuments';

const isReceivedBefore =
	(days: number) => (message: DatoveSchrankyDatovaZpravaDto) => {
		const dateString = message.datumACasDoruceni || message.datumACasDodani;
		if (!dateString) return false;
		const date = new Date(dateString);
		const now = new Date();
		const diffDays = dateDiffInDays(now, date);

		return diffDays > days;
	};

const isReceivedBefore90Days = isReceivedBefore(90);
export const isReceivedBefore10Days = isReceivedBefore(10);

interface MessageOperationsProps {
	dataBoxId: string;
	folderType: DataBoxFolderType;
	messageId: number;
	message?: DatoveSchrankyDatovaZpravaDto;
	updateLoadingItems: (
		loadingItem: DataBoxMessageDetailLoadingItemType,
		isLoading: boolean
	) => void;
	setConfirmModal: (confirmModal: Nullable<DataBoxConfirmModalType>) => void;
}

export const useMessageOperations = ({
	dataBoxId,
	folderType,
	messageId,
	message,
	updateLoadingItems,
	setConfirmModal,
}: MessageOperationsProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tn = getLocalizeCurried(PageCode['datove-schranky']);
	const queryClient = useQueryClient();
	const { axiosInstance } = useAxiosInstance();
	const { storageRefetch } = useDataBoxEvents();
	const { toastMessageSuccess } = useMessageEvents();
	const { messagesArchive, messageListUpdate } = useDataBoxEvents();

	const archivingSettingsQuery = useArchivingSettingsQuery(dataBoxId);

	const isArchivingEnabled =
		archivingSettingsQuery.data?.hodnota ===
			DatovkaNastaveniArchivaceValue.LargeDataboxMessage &&
		(message ? !isReceivedBefore90Days(message) : false);

	const archiveMessage = usePoMutation({
		mutationFn: archiveDataBoxesMessageQuery(axiosInstance),
		onSuccess: () => {
			messagesArchive({ messageIds: [messageId] });
			updateLoadingItems(DataBoxMessageDetailLoadingItemType.Archive, false);
			storageRefetch();

			toastMessageSuccess(tn('akce.zprava-zkopirovana'), {
				icon: {
					name: 'archived-outline',
					type: 'templates',
				},
			});
		},
		onError: async () => {
			updateLoadingItems(DataBoxMessageDetailLoadingItemType.Archive, false);
		},
	});

	const handleArchiveMessage = () => {
		updateLoadingItems(DataBoxMessageDetailLoadingItemType.Archive, true);
		archiveMessage.mutate({ dataBoxId, messageId });
	};

	const restoreMessage = usePoMutation({
		mutationFn: restoreDataBoxesMessageQuery(axiosInstance),
		onSuccess: (_, variables) => {
			messageListUpdate();
			updateLoadingItems(DataBoxMessageDetailLoadingItemType.Restore, false);

			toastMessageSuccess(tn('akce.zprava-obnovena'), {
				icon: {
					name: 'trash-restore',
					type: 'templates',
				},
			});

			variables.redirect();
		},
		onError: async () => {
			updateLoadingItems(DataBoxMessageDetailLoadingItemType.Restore, false);
		},
	});

	const handleRestoreMessage = (redirect: () => void) => {
		updateLoadingItems(DataBoxMessageDetailLoadingItemType.Restore, true);
		restoreMessage.mutate({ messageId, redirect });
	};

	const deleteMessage = usePoMutation({
		mutationFn: deleteDataBoxesMessageQuery(axiosInstance),
		onSuccess: (_, variables) => {
			messageListUpdate();
			storageRefetch();

			updateLoadingItems(
				variables.permanently
					? DataBoxMessageDetailLoadingItemType.RemovePermanently
					: DataBoxMessageDetailLoadingItemType.Remove,
				false
			);

			setConfirmModal(null);

			toastMessageSuccess(
				variables.permanently
					? tn('akce.zprava-trvale-smazana')
					: tn('akce.zprava-smazana-z-archivu'),
				{
					icon: {
						name: 'trash',
						type: 'templates',
					},
				}
			);

			variables.redirect();
		},
		onError: async (_, variables) => {
			updateLoadingItems(
				variables.permanently
					? DataBoxMessageDetailLoadingItemType.RemovePermanently
					: DataBoxMessageDetailLoadingItemType.Remove,
				false
			);
		},
	});

	const handleDeleteMessage = (permanently = false, redirect: () => void) => {
		updateLoadingItems(
			permanently
				? DataBoxMessageDetailLoadingItemType.RemovePermanently
				: DataBoxMessageDetailLoadingItemType.Remove,
			true
		);
		deleteMessage.mutate({ messageId, permanently, redirect });
	};

	const deleteMessageFromVault = usePoMutation({
		mutationFn: deleteDataBoxesMessageFromVaultQuery(axiosInstance),
		onSuccess: (_, variables) => {
			messageListUpdate();
			storageRefetch();

			updateLoadingItems(
				DataBoxMessageDetailLoadingItemType.DeleteFromVault,
				false
			);

			setConfirmModal(null);

			toastMessageSuccess(tn('akce.zprava-smazana-z-trezoru'), {
				icon: {
					name: 'trash',
					type: 'native',
				},
			});

			variables.redirect();
		},
		onError: async () => {
			updateLoadingItems(
				DataBoxMessageDetailLoadingItemType.DeleteFromVault,
				false
			);
		},
	});

	const handleDeleteMessageFromVault = (
		direction: 'ODESLANA' | 'PRIJATA',
		redirect: () => void
	) => {
		updateLoadingItems(
			DataBoxMessageDetailLoadingItemType.DeleteFromVault,
			true
		);
		deleteMessageFromVault.mutate({
			messageId,
			direction,
			dataBoxId,
			redirect,
		});
	};

	const { downloadDataBoxMessage } = useDownloadEvents();

	const handleDownloadMessage = (message: DatoveSchrankyDatovaZpravaDto) => {
		updateLoadingItems(DataBoxMessageDetailLoadingItemType.Download, true);

		downloadDataBoxMessage({
			messageData: { dataBoxId, message, folderType },
			callback: () => {
				updateLoadingItems(DataBoxMessageDetailLoadingItemType.Download, false);
			},
		});
	};

	const saveAllAttachmentsToDocuments = useSaveAllAttachmentsToDocuments({
		onError: () => {
			updateLoadingItems(
				DataBoxMessageDetailLoadingItemType.SaveAttachmentsToDocuments,
				false
			);
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['documents'] });

			updateLoadingItems(
				DataBoxMessageDetailLoadingItemType.SaveAttachmentsToDocuments,
				false
			);
			storageRefetch();
		},
	});

	const handleSaveAllAttachmentsToDocuments = (
		message: DatoveSchrankyDatovaZpravaDto
	) => {
		updateLoadingItems(
			DataBoxMessageDetailLoadingItemType.SaveAttachmentsToDocuments,
			true
		);

		saveAllAttachmentsToDocuments.mutate({
			attachments: message.prilohy ?? [],
			dataBoxId,
			messageId,
			folderType,
		});
	};

	return {
		isLoading: archivingSettingsQuery.isLoading,
		isArchivingEnabled,
		handleArchiveMessage,
		handleRestoreMessage,
		handleDeleteMessage,
		handleDownloadMessage,
		handleSaveAllAttachmentsToDocuments,
		handleDeleteMessageFromVault,
	};
};
